import { postSkyflowFileUpload } from "config/skyflow";
import { FocusError } from "focus-formik-error";
import { useFormik } from "formik";
import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";
import { postPhysicianDocumentShare } from "services/api/taptype/patients";
import { actions as flashesActions } from "store/flashes";
import {
	apiHandler,
	errorResponseInterceptor,
	isFunction,
	objectToFormData,
	successResponseInterceptor,
	toSnakeCase,
} from "utils/helpers";
import { useCollectContainer } from "utils/skyflowProxyContainers";
import vars from "utils/vars";
import { array, mixed, object, string } from "yup";

const DOCUMENT_COLUMN_NAMES_MAP = {
	RX: "rx_file",
	EDU_INFO: "edu_info_file",
	REFERRALS: "order_file",
	LAB_RESULTS: "result_file",
	DIAGNOSTIC_TEST: "result_file",
	DIAGNOSTIC_IMAGING: "result_file",
	OTHER: "other_file",
};

const stripExtension = (filename = "") => filename.replace(/\.[^.]+$/, "");

const generateRowId = () =>
	`doc-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

const HcpShareDocument = ({ onSuccessCb, patientId }) => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const { t, i18n } = useTranslation();
	const skyFlowContainer = useCollectContainer();

	const postPhysicianDocumentShareCancelRequestRef = useRef(null);

	const uniqueId = useId();

	const [isUploadingDocumentLoadingState, setIsUploadingDocumentLoadingState] = useState(false);
	const [isNotifyCheckedState, setIsNotifyCheckedState] = useState(false);
	const [rowStatusesState, setRowStatusesState] = useState({});

	const collectSkyflowRecord = () =>
		new Promise((resolve, reject) => {
			apiHandler(skyFlowContainer.collect, {
				data: {
					additionalFields: {
						records: [{ table: vars.app.skyflowTables.patientOrder, fields: { patient_id: patientId } }],
					},
				},
				onError: (err) => reject(err),
				onSuccess: (response) => {
					successResponseInterceptor(response, { dispatch, language: i18n.language, t });
					const firstRecord = response?.records?.[0];
					const recordData = firstRecord?.data ?? firstRecord?.fields ?? firstRecord;
					const skyflowID =
						recordData?.skyflow_id ??
						recordData?.id ??
						firstRecord?.skyflow_id ??
						firstRecord?.id ??
						response?.skyflow_id ??
						null;
					if (!skyflowID) return reject(new Error("missing_skyflow_id"));
					resolve(skyflowID);
				},
			});
		});

	const uploadFileToSkyflow = (skyflowID, columnName, document) =>
		new Promise((resolve, reject) => {
			apiHandler(postSkyflowFileUpload, {
				data: {
					table: vars.app.skyflowTables.patientOrder,
					skyflowID,
					body: objectToFormData(
						{ [columnName]: document },
						null,
						null,
						toSnakeCase(document?.name || "")
					),
				},
				onError: (err) => reject(err),
				onSuccess: (response) => {
					successResponseInterceptor(response, { dispatch, language: i18n.language, t });
					resolve(response);
				},
			});
		});

	const sharePhysicianDocumentsBatch = (body, signal) =>
		new Promise((resolve, reject) => {
			apiHandler(postPhysicianDocumentShare, {
				data: {
					params: { id: patientId },
					body,
					options: { signal },
				},
				onError: (err) => reject(err),
				onSuccess: (response) =>
					resolve(Array.isArray(response?.data) ? response.data : response?.data ? [response.data] : []),
			});
		});

	// Per-file Skyflow upload — each file needs its own vault row before we can batch-share.
	const uploadRowToSkyflow = async (row) => {
		const columnName = DOCUMENT_COLUMN_NAMES_MAP[row?.file_type];
		if (!columnName) throw new Error("invalid_file_type");

		const skyflowID = await collectSkyflowRecord();
		await uploadFileToSkyflow(skyflowID, columnName, row.document);
		return skyflowID;
	};

	const onHcpShareDocumentFormikSubmitHandler = async (values, { setErrors, resetForm }) => {
		if (!patientId) return;

		const rows = Array.isArray(values?.documents) ? values.documents : [];
		if (!rows.length) return;

		setIsUploadingDocumentLoadingState(true);

		const skyflowReadyRows = [];
		const failedRows = [];
		const nextStatuses = { ...rowStatusesState };

		// Step 1: per-file Skyflow upload (sequential).
		for (const row of rows) {
			nextStatuses[row._id] = { status: "uploading", error: "" };
			setRowStatusesState({ ...nextStatuses });

			try {
				const skyflowID = await uploadRowToSkyflow(row);
				skyflowReadyRows.push({ row, skyflowID });
			} catch (err) {
				errorResponseInterceptor(err, {
					dispatch,
					navigate,
					language: i18n.language,
					t,
				});
				nextStatuses[row._id] = {
					status: "failed",
					error: err?.message || t("system_messages.something_went_wrong_try_again"),
				};
				setRowStatusesState({ ...nextStatuses });
				failedRows.push(row);
			}
		}

		// Step 2: single batched share call for everything that uploaded successfully.
		let createdDocs = [];
		if (skyflowReadyRows.length) {
			if (postPhysicianDocumentShareCancelRequestRef.current?.signal)
				postPhysicianDocumentShareCancelRequestRef.current?.abort();
			postPhysicianDocumentShareCancelRequestRef.current = new AbortController();

			const documentsPayload = skyflowReadyRows.map(({ row, skyflowID }) => ({
				name: (row?.name || "").trim() || stripExtension(row?.document?.name || "") || "document",
				file_type: row.file_type,
				skyflow_id: skyflowID,
			}));

			const body = {
				documents: documentsPayload,
				notify: !!values.notify,
				...(values.notify && values.notification_type ? { notification_type: values.notification_type } : {}),
			};

			try {
				createdDocs = await sharePhysicianDocumentsBatch(
					body,
					postPhysicianDocumentShareCancelRequestRef.current.signal
				);
				skyflowReadyRows.forEach(({ row }) => {
					nextStatuses[row._id] = { status: "done", error: "" };
				});
				setRowStatusesState({ ...nextStatuses });
			} catch (err) {
				if (err?.errors) setErrors(err.errors);
				else
					errorResponseInterceptor(err, {
						dispatch,
						navigate,
						language: i18n.language,
						t,
					});
				skyflowReadyRows.forEach(({ row }) => {
					nextStatuses[row._id] = {
						status: "failed",
						error: err?.message || t("system_messages.something_went_wrong_try_again"),
					};
					failedRows.push(row);
				});
				setRowStatusesState({ ...nextStatuses });
			}
		}

		setIsUploadingDocumentLoadingState(false);

		if (!failedRows.length) {
			resetForm();
			setIsNotifyCheckedState(false);
			setRowStatusesState({});
			if (isFunction(onSuccessCb)) onSuccessCb(createdDocs);
			return;
		}

		hcpShareDocumentFormik.setFieldValue("documents", failedRows);

		if (createdDocs.length) {
			dispatch(
				flashesActions.add({
					type: "warning",
					message: t("system_messages.partial_upload_failure", {
						defaultValue: `${createdDocs.length} document(s) uploaded, ${failedRows.length} failed. Please retry the failed ones.`,
					}),
					id: "hcp-upload-document-partial-failure",
				})
			);
		}
	};

	const hcpShareDocumentFormik = useFormik({
		enableReinitialize: true,
		initialValues: { documents: [], notify: false, notification_type: "" },
		validationSchema: object().shape({
			documents: array()
				.of(
					object().shape({
						name: string().nullable(),
						file_type: string()
							.required(t("system_messages.required_field"))
							.oneOf(vars.documents.types.map(({ value }) => value)),
						document: mixed()
							.test(
								"size",
								t("system_messages.file_size_validation", { mazSize: vars.app.fileMaxSizeInMB }),
								(file) =>
									!!!file ||
									(!!file?.size && file.size <= vars.app.fileMaxSizeInMB * 1024 * 1024)
							)
							.test("type", t("system_messages.file_format_validation"), (file) =>
								!!!file || (!!file?.type && vars.app.documentUploadMimeTypes.includes(file.type))
							)
							.required(t("system_messages.required_field")),
					})
				)
				.min(1, t("system_messages.required_field")),
			notification_type: string()
				.nullable()
				.when("notify", {
					is: true,
					then: (schema) => schema.required(t("system_messages.required_field")).oneOf(["email", "sms"]),
				}),
		}),
		onSubmit: onHcpShareDocumentFormikSubmitHandler,
	});

	const handleAddFiles = (fileList) => {
		const incoming = Array.from(fileList || []);
		if (!incoming.length) return;
		const newRows = incoming.map((file) => ({
			_id: generateRowId(),
			name: stripExtension(file?.name || ""),
			file_type: "",
			document: file,
		}));
		hcpShareDocumentFormik.setFieldValue("documents", [
			...(hcpShareDocumentFormik.values.documents || []),
			...newRows,
		]);
	};

	const handleRemoveRow = (rowId) => {
		hcpShareDocumentFormik.setFieldValue(
			"documents",
			(hcpShareDocumentFormik.values.documents || []).filter((row) => row._id !== rowId)
		);
		setRowStatusesState((prev) => {
			const next = { ...prev };
			delete next[rowId];
			return next;
		});
	};

	const handleNotifyToggle = ({ target: { checked } }) => {
		setIsNotifyCheckedState(checked);
		hcpShareDocumentFormik.setFieldValue("notify", checked);
		// Email is currently the only supported notification channel, so auto-select it
		// to satisfy the requirement and avoid an extra click for the user.
		hcpShareDocumentFormik.setFieldValue("notification_type", checked ? "email" : "");
		if (!checked) hcpShareDocumentFormik.setFieldTouched("notification_type", false, false);
	};

	useEffect(() => {
		return () => {
			if (postPhysicianDocumentShareCancelRequestRef.current?.signal)
				postPhysicianDocumentShareCancelRequestRef.current?.abort();
		};
	}, []);

	const documents = hcpShareDocumentFormik.values.documents || [];
	const documentsErrors = hcpShareDocumentFormik.errors?.documents;
	const documentsTouched = hcpShareDocumentFormik.touched?.documents;

	return (
		<form onSubmit={hcpShareDocumentFormik.handleSubmit} noValidate>
			<FocusError formik={hcpShareDocumentFormik} />
			<fieldset disabled={isUploadingDocumentLoadingState}>
				<legend className="visually-hidden">HCP upload document form: </legend>
				<div className="row gy-4 mt-0">
					<div className="col-12 mt-0">
						<label
							htmlFor={`documentsField-${uniqueId}`}
							className="form-label text-capitalize visually-hidden">
							<small>
								{t("fields.upload_document")} <span className="ms-1">*</span>
							</small>
						</label>
						<div className="row flex-nowrap gx-1 align-items-center">
							<div className="col-auto">
								<span className="text-primary">
									<svg
										xmlns="http://www.w3.org/2000/svg"
										width="22"
										height="11"
										viewBox="0 0 22 11"
										fill="currentColor"
										className="rotate-n45">
										<path
											d="M18.5,16H7A4,4,0,0,1,7,8H19.5a2.5,2.5,0,0,1,0,5H9a1,1,0,0,1,0-2h9.5V9.5H9a2.5,2.5,0,0,0,0,5H19.5a4,4,0,0,0,0-8H7a5.5,5.5,0,0,0,0,11H18.5Z"
											transform="translate(-1.5 -6.5)"
										/>
									</svg>
								</span>
							</div>
							<div className="col-11">
								<input
									onChange={({ target: { files } }) => {
										handleAddFiles(files);
										hcpShareDocumentFormik.setFieldTouched("documents", true);
									}}
									className="form-control"
									name="documents"
									type="file"
									id={`documentsField-${uniqueId}`}
									accept={vars.app.documentUploadMimeTypes.join(",")}
									aria-describedby={`documentsFieldHelp-${uniqueId}`}
									autoComplete="off"
									multiple
									hidden
								/>
								<label
									htmlFor={`documentsField-${uniqueId}`}
									className="btn btn-link w-100 p-0 border-0 rounded-0 text-uppercase text-decoration-none text-start">
									<strong>
										<span>
											{documents.length > 0
												? t("actions.add_more", { defaultValue: "Add more files" })
												: t("fields.upload_document")}
										</span>
									</strong>
								</label>
								<div id={`documentsFieldHelp-${uniqueId}`} className="form-text mt-0">
									{t("system_messages.document_upload_format_validation_size", {
										size: vars.app.fileMaxSizeInMB,
									})}
								</div>
								{typeof documentsErrors === "string" && documentsTouched && (
									<div className="invalid-feedback d-block text-uppercase">
										<strong>{documentsErrors}</strong>
									</div>
								)}
							</div>
						</div>
					</div>

					{documents.map((row, index) => {
						const rowStatus = rowStatusesState[row._id]?.status;
						const rowError = rowStatusesState[row._id]?.error;
						const rowFieldErrors = Array.isArray(documentsErrors) ? documentsErrors[index] : null;
						const rowFieldTouched = Array.isArray(documentsTouched) ? documentsTouched[index] : null;
						return (
							<div className="col-12" key={row._id}>
								<div className="border rounded p-3 bg-light-subtle">
									<div className="row gy-3">
										<div className="col-12 d-flex align-items-center gap-2">
											<span className="flex-grow-1 text-truncate" title={row.document?.name}>
												<strong>
													<small>{row.document?.name || "-"}</small>
												</strong>
											</span>
											{rowStatus === "uploading" && (
												<span
													className="spinner-border spinner-border-sm text-primary"
													role="status">
													<span className="visually-hidden">
														{`${t("content.loading")}...`}
													</span>
												</span>
											)}
											{rowStatus === "done" && (
												<span className="badge text-bg-success text-uppercase">
													<small>{t("actions.uploaded", { defaultValue: "Uploaded" })}</small>
												</span>
											)}
											{rowStatus === "failed" && (
												<span className="badge text-bg-danger text-uppercase">
													<small>{t("actions.failed", { defaultValue: "Failed" })}</small>
												</span>
											)}
											<button
												type="button"
												onClick={() => handleRemoveRow(row._id)}
												disabled={rowStatus === "uploading"}
												className="btn btn-sm btn-link text-danger p-0 border-0"
												aria-label={t("actions.remove", { defaultValue: "Remove" })}
												tabIndex="0">
												<small>
													<strong>
														{t("actions.remove", { defaultValue: "Remove" })}
													</strong>
												</small>
											</button>
										</div>
										<div className="col-12 col-md-6">
											<div className="form-floating">
												<input
													onChange={(e) =>
														hcpShareDocumentFormik.setFieldValue(
															`documents[${index}].name`,
															e.target.value
														)
													}
													onBlur={() =>
														hcpShareDocumentFormik.setFieldTouched(
															`documents[${index}].name`,
															true
														)
													}
													value={row?.name || ""}
													type="text"
													name={`documents[${index}].name`}
													id={`nameField-${uniqueId}-${row._id}`}
													className="form-control rounded-0 border-top-0 border-start-0 border-end-0 text-truncate"
													placeholder={t("fields.document_name")}
												/>
												<label
													htmlFor={`nameField-${uniqueId}-${row._id}`}
													className="form-label text-capitalize border-0 mb-0 text-secondary">
													<small>{t("fields.document_name")}</small>
												</label>
											</div>
										</div>
										<div className="col-12 col-md-6">
											<div className="form-floating">
												<select
													onChange={(e) =>
														hcpShareDocumentFormik.setFieldValue(
															`documents[${index}].file_type`,
															e.target.value
														)
													}
													onBlur={() =>
														hcpShareDocumentFormik.setFieldTouched(
															`documents[${index}].file_type`,
															true
														)
													}
													value={row?.file_type || ""}
													name={`documents[${index}].file_type`}
													className={`form-select rounded-0 border-top-0 border-start-0 border-end-0 text-truncate ${
														rowFieldTouched?.file_type && rowFieldErrors?.file_type
															? "is-invalid"
															: row?.file_type && !rowFieldErrors?.file_type
															? "is-valid"
															: ""
													}`}
													id={`fileTypeField-${uniqueId}-${row._id}`}
													required>
													<option value="" disabled>
														{`-${t("fields.select_file_type")}-`}
													</option>
													{[...(vars.documents.types || [])]?.map(({ label = "", value = "" }) => (
														<option value={value} key={value}>
															{label}
														</option>
													))}
												</select>
												<label
													htmlFor={`fileTypeField-${uniqueId}-${row._id}`}
													className="form-label text-capitalize border-0 mb-0 text-secondary">
													<small>
														{t("fields.file_type")} <span className="ms-1">*</span>
													</small>
												</label>
												{rowFieldTouched?.file_type && rowFieldErrors?.file_type && (
													<div className="invalid-feedback text-uppercase">
														<strong>{rowFieldErrors.file_type}</strong>
													</div>
												)}
											</div>
										</div>
										{rowError && (
											<div className="col-12">
												<div className="alert alert-danger mb-0 py-2">
													<small>
														<strong>{rowError}</strong>
													</small>
												</div>
											</div>
										)}
									</div>
								</div>
							</div>
						);
					})}

					<div className="col-12">
						<div className="form-check">
							<input
								onChange={handleNotifyToggle}
								value="1"
								className="form-check-input"
								type="checkbox"
								id={`notifyPatientField-${uniqueId}`}
								name="notify"
								autoComplete="off"
								checked={isNotifyCheckedState}
							/>
							<label
								className="form-check-label text-capitalize"
								htmlFor={`notifyPatientField-${uniqueId}`}>
								<small>Share With Patient</small>
							</label>
						</div>
						{isNotifyCheckedState && (
							<div className="mt-3">
								<div className="form-floating">
									<select
										onChange={hcpShareDocumentFormik.handleChange}
										onBlur={hcpShareDocumentFormik.handleBlur}
										value={hcpShareDocumentFormik.values?.notification_type || ""}
										name="notification_type"
										className={`form-select rounded-0 border-top-0 border-start-0 border-end-0 text-truncate ${
											!!hcpShareDocumentFormik.touched?.notification_type &&
											!!hcpShareDocumentFormik.errors?.notification_type
												? "is-invalid"
												: hcpShareDocumentFormik.values?.notification_type &&
												  !hcpShareDocumentFormik.errors?.notification_type
												? "is-valid"
												: ""
										}`}
										id={`notificationTypeField-${uniqueId}`}
										required>
										<option value="" disabled>
											-Select Notification Type-
										</option>
										<option value="email">Email</option>
										{/* <option value="sms">SMS</option> */}
									</select>
									<label
										htmlFor={`notificationTypeField-${uniqueId}`}
										className="form-label text-capitalize border-0 mb-0 text-secondary">
										<small>
											Notification Type <span className="ms-1">*</span>
										</small>
									</label>
									{!!hcpShareDocumentFormik.touched?.notification_type &&
										!!hcpShareDocumentFormik.errors?.notification_type && (
											<div className="invalid-feedback text-uppercase">
												<strong>{hcpShareDocumentFormik.errors.notification_type}</strong>
											</div>
										)}
								</div>
							</div>
						)}
					</div>

					<div className="col-12 mt-5">
						<button
							className="btn btn-primary w-100 text-uppercase"
							type="submit"
							disabled={!documents.length}>
							{isUploadingDocumentLoadingState ? (
								<span className="spinner-border spinner-border-sm" role="status">
									<span className="visually-hidden">{`${t("content.loading")}...`}</span>
								</span>
							) : (
								<strong>
									<small>{t("actions.upload")}</small>
								</strong>
							)}
						</button>
					</div>
				</div>
			</fieldset>
		</form>
	);
};

export default HcpShareDocument;
